import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ApiError, SessionExpired, api, type EpicSummary } from '../api';
import { progressDenominator } from './status';

/**
 * Every epic, with the two things the board never showed: whether anyone has said what
 * done means, and how much of it is met.
 */

function NewEpicDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const close = () => {
    setName('');
    setGoal('');
    setProblem(null);
    onClose();
  };

  const create = async () => {
    setSaving(true);
    setProblem(null);
    try {
      await api.createEpic({ name: name.trim(), goal });
      onCreated();
      close();
    } catch (cause) {
      if (cause instanceof SessionExpired) return;
      setProblem(
        cause instanceof ApiError && cause.fieldErrors.length
          ? cause.fieldErrors.join(' ')
          : cause instanceof Error
            ? cause.message
            : 'Could not create the epic.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>New epic</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {problem && <Alert severity="error">{problem}</Alert>}
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            // The name is the key every ticket in the epic will store, so it is fixed
            // once tickets exist. Saying so here is cheaper than a rename that cannot
            // be offered.
            helperText="This is the epic's identity and cannot be changed later."
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="Goal (markdown, optional)"
            value={goal}
            onChange={(event) => setGoal(event.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void create()} disabled={saving || !name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EpicCard({ epic, onOpen }: { epic: EpicSummary; onOpen: () => void }) {
  const stated = epic.criteriaTotal > 0;
  return (
    <Card variant="outlined" sx={{ opacity: epic.archived ? 0.6 : 1 }}>
      <CardActionArea onClick={onOpen} sx={{ p: 2, height: '100%', alignItems: 'stretch' }}>
        <Stack spacing={1} sx={{ height: '100%' }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" fontWeight={600}>
              {epic.name}
            </Typography>
            {epic.archived && <Chip size="small" label="Archived" />}
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: 40,
            }}
          >
            {epic.goal || 'No goal stated.'}
          </Typography>

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            <LinearProgress
              variant="determinate"
              value={stated ? (epic.criteriaMet / epic.criteriaTotal) * 100 : 0}
              color={stated && epic.criteriaMet === epic.criteriaTotal ? 'success' : 'primary'}
              sx={{ flex: 1, height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {stated ? `${epic.criteriaMet}/${epic.criteriaTotal} met` : 'no criteria'}
            </Typography>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {epic.doneCount} of {progressDenominator(epic)} tickets done
            {epic.closedCount > 0 && ` · ${epic.closedCount} closed`}
          </Typography>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export function EpicsView({
  onOpenEpic,
  onChanged,
}: {
  onOpenEpic: (name: string) => void;
  /** Creating an epic changes `/v1/meta`, which the board's filters read. */
  onChanged: () => void;
}) {
  const [epics, setEpics] = useState<EpicSummary[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setProblem(null);
    try {
      setEpics((await api.listEpics()).epics);
    } catch (cause) {
      if (cause instanceof SessionExpired) return;
      setProblem(cause instanceof Error ? cause.message : 'Could not load epics.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {epics ? `${epics.length} epics` : ' '}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" onClick={() => setCreating(true)}>
          New epic
        </Button>
      </Stack>

      {problem && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => void load()}>Retry</Button>}>
          {problem}
        </Alert>
      )}

      {epics ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          {epics.map((epic) => (
            <EpicCard key={epic.name} epic={epic} onOpen={() => onOpenEpic(epic.name)} />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      <NewEpicDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          void load();
          onChanged();
        }}
      />
    </Box>
  );
}
